import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface VersionSelectorProps {
  /** The currently active/displayed version string (e.g. "v2.3"). */
  currentVersion: string
  /**
   * The versions to list, newest first. The caller is responsible for any
   * filtering (e.g. restricting to the supported major version) before passing
   * them in — this component renders exactly what it is given.
   */
  versions: string[]
  /** Callback when the user selects a different version from the list. */
  onVersionChange: (version: string) => void
  /**
   * Which side of the trigger the options popover opens toward
   */
  side?: "top" | "bottom"
  /**
   * Extra classes merged onto the Select trigger.
   */
  triggerClassName?: string
  /** Accessible label for the trigger. Defaults to "Dataset version". */
  ariaLabel?: string
}

/**
 * VersionSelector renders a Select dropdown listing dataset versions, used to
 * switch the active parasite rate dataset version. It is layout-agnostic: it
 * renders only the dropdown, so the caller owns positioning (map overlay vs.
 * table toolbar) by wrapping or styling it as needed.
 *
 * The versions list is rendered as-is; the caller performs any filtering before
 * passing it in.
 *
 * @param props - Current version, the versions to list, change handler, and
 *   optional trigger styling/label overrides.
 * @returns A version selector dropdown element.
 */
export default function VersionSelector({
  currentVersion,
  versions,
  onVersionChange,
  side = "top",
  triggerClassName,
  ariaLabel = "Dataset version",
}: VersionSelectorProps): React.JSX.Element {
  return (
    <Select value={currentVersion} onValueChange={onVersionChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn("h-8 w-auto text-xs", triggerClassName)}
      >
        <SelectValue placeholder="Version" />
      </SelectTrigger>
      <SelectContent
        position="popper"
        side={side}
        align="start"
        sideOffset={4}
        className="min-w-0 w-auto max-h-48 overflow-y-auto"
      >
        {versions.map((version) => (
          <SelectItem key={version} value={version}>
            {version}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
